import type { Box } from "./framing"

// Where the whole car should sit for the best photo: as big as the screen
// allows between the instruction and the shutter, with a small margin all
// round (the saved photo is the full sensor frame, so it has a little more).
// Shaped like the car the camera actually sees (width over height measured
// from its traced outline), never like a drawing. Pure; unit-tested.

export interface Insets {
  top: number
  right: number
  bottom: number
  left: number
}

export interface TargetFrame {
  // The ideal place and size for the car (0–1 of the visible frame).
  box: Box
  // The car must stay inside this (0–1): a car past it is about to be cut off.
  safe: Box
  // The car must be at least this share of the ideal width.
  minFill: number
}

export const TARGET = {
  // Margin: this share of the usable area's short side.
  MARGIN: 0.05,
  MIN_FILL: 0.8,
  // A car seen from a corner at eye level, before one has been measured
  // (real photos: 1.4 to 1.6 wide for each unit of height).
  DEFAULT_CAR_ASPECT: 1.5,
  // How fast the measured shape follows the car (per trace).
  ASPECT_EMA: 0.3,
  // The target never gets less than this share of the screen's height or
  // width, however much the instruction and the controls cover (a phone
  // held sideways): the car then sits partly behind them, but is never tiny.
  MIN_USABLE: 0.5,
} as const

export function targetFrame(
  screen: { w: number; h: number },
  insets: Insets,
  carAspect: number = TARGET.DEFAULT_CAR_ASPECT,
): TargetFrame {
  const W = Math.max(1, screen.w)
  const H = Math.max(1, screen.h)
  // Insets that would leave too little are scaled down until enough is left.
  const fit = (a: number, b: number, size: number) => {
    const covered = a + b
    const max = size * (1 - TARGET.MIN_USABLE)
    const k = covered > max && covered > 0 ? max / covered : 1
    return [a * k, b * k] as const
  }
  const [top, bottom] = fit(insets.top, insets.bottom, H)
  const [left, right] = fit(insets.left, insets.right, W)
  const ux = left
  const uy = top
  const uw = Math.max(1, W - left - right)
  const uh = Math.max(1, H - top - bottom)
  const m = TARGET.MARGIN * Math.min(uw, uh)
  const aspect = carAspect > 0 ? carAspect : TARGET.DEFAULT_CAR_ASPECT
  const bw = Math.max(1, Math.min(uw - 2 * m, (uh - 2 * m) * aspect))
  const bh = bw / aspect
  const bx = ux + (uw - bw) / 2
  const by = uy + (uh - bh) / 2
  return {
    box: { x: bx / W, y: by / H, w: bw / W, h: bh / H },
    safe: { x: (ux + m / 2) / W, y: (uy + m / 2) / H, w: (uw - m) / W, h: (uh - m) / H },
    minFill: TARGET.MIN_FILL,
  }
}

// The car's shape (width over height, in pixels) as seen so far.
export function nextCarAspect(prev: number | null, car: Box, screen: { w: number; h: number }): number {
  const seen = (car.w * screen.w) / Math.max(1e-6, car.h * screen.h)
  if (!Number.isFinite(seen) || seen <= 0) return prev ?? TARGET.DEFAULT_CAR_ASPECT
  // Clamp to what cars can look like (a bus front to a low coupé's side).
  const clamped = Math.min(3.5, Math.max(0.8, seen))
  return prev === null ? clamped : prev + (clamped - prev) * TARGET.ASPECT_EMA
}
